<template>
  <div>
    <div id="app">
      <div data-pc-banner="flexiquote"></div>
      <app-errors />
      <div v-if="requireAuth"
        id="auth-content">
        <app-nav />
        <router-view v-if="$userInfo && $user && $user.permissions && $company"
          class="app-content" />
      </div>
      <div v-else>
        <router-view />
      </div>
    </div>
  </div>

</template>

<script>
import AppNav from './AppNav.vue'
import AppErrors from './AppErrors.vue'
import Vue from 'vue'
import VueTheMask from 'vue-the-mask'
import Vuelidate from 'vuelidate'
import GlobalSpinnerMixin from '@/components/mixins/global/GlobalSpinnerMixin'
import GlobalUserInfoMixin from '@/components/mixins/global/GlobalUserInfoMixin'
import GlobalCompanyMixin from '@/components/mixins/global/GlobalCompanyMixin'
import GlobalLicenceInfoMixin from '@/components/mixins/global/GlobalLicenceInfoMixin'
import GlobalCommonMixin from '@/components/mixins/global/GlobalCommonMixin'
import GlobalUserSettingMixin from '@/components/mixins/global/GlobalUserSettingMixin'
import GlobalNotificationMixin from '@/components/mixins/global/GlobalNotificationMixin'
import GlobalThemeMixin from '@/components/mixins/global/GlobalThemeMixin'
import AppLink from '@/components/AppLink/AppLink.vue'
import BulmaSnackbar from '@/components/BulmaSnackbar'
import BulmaToast from '@/components/BulmaToast'
import UnsavedChangesModalPlugin from '@/components/BulmaModal/UnsavedChangesModalPlugin'
import VCalendar from 'v-calendar'
import filters from '@/filters'
import Tippy, { TippyComponent } from '@/components/Tippy'
// import 'v-calendar/lib/v-calendar.min.css'
// import { setupCalendar } from 'v-calendar'

// Use v-calendar, v-date-picker & v-popover components
Vue.use(VCalendar, {
  locale: 'en-AU',
  locales: {
    'en-MY': {
      firstDayOfWeek: 1,
      masks: {
        L: 'DD/MM/YYYY'
        // ...optional `title`, `weekdays`, `navMonths`, etc
      }
    }
  },
  datePicker: {
    popover: {
      visibility: 'focus'
    }
  },
  masks: {
    title: 'MMMM YYYY',
    weekdays: 'W',
    navMonths: 'MMM',
    input: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
    data: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
    dayPopover: 'L'
  }
})

Vue.use(VueTheMask) // Added to fix error when used in lazy-loaded components
Vue.use(Vuelidate)
Vue.use(BulmaSnackbar)
Vue.use(BulmaToast)
Vue.use(UnsavedChangesModalPlugin)
Vue.use(Tippy)
Vue.component('tippy', TippyComponent)
// The standard way to render a link. Global so no file needs to import it.
Vue.component('app-link', AppLink)

Vue.mixin(GlobalSpinnerMixin)
Vue.mixin(GlobalUserInfoMixin)
Vue.mixin(GlobalLicenceInfoMixin)
Vue.mixin(GlobalCommonMixin)
Vue.mixin(GlobalUserSettingMixin)
Vue.mixin(GlobalCompanyMixin)
Vue.mixin(GlobalNotificationMixin)
Vue.mixin(GlobalThemeMixin)

Vue.prototype.$eventHub = new Vue()
// For Vue 3.x compatibilty. Filters are removed in 3.x
Vue.prototype.$filters = filters

export default {
  name: 'App',
  components: {
    AppNav,
    AppErrors
  },
  data() {
    return {
      requireAuth: false
    }
  },
  computed: {
    bypassAuth() {
      return this.$route.meta && this.$route.meta.bypassAuth
    }
  },
  mounted() {
    document.documentElement.className = `theme-${this.$theme.name}`
    this._bannerObserver = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect.height || 0
      document.documentElement.style.setProperty('--pc-banner-height', `${height}px`)
    })
    const banner = this.$el.querySelector('[data-pc-banner]')
    if (banner) this._bannerObserver.observe(banner)
  },
  async created() {
    this.$router.onReady(this.loadResources)
  },
  beforeDestroy() {
    this.stopNotificationPoll()
    if (this._bannerObserver) this._bannerObserver.disconnect()
  },

  methods: {
    async loadResources() {
      if (this.bypassAuth) return
      this.requireAuth = true

      await Promise.all([this.getUserInfo(), this.getUserPermissions(), this.getCompanyItemCategories(), this.getCompanyJobNSubJobStage(), this.getCompanySetting(), this.getCompanyInfo()])
      this.getLabourTypes()
      this.getNotifications()
      this.startNotificationPoll()
      this.$store.dispatch('vehicleOptions/load')
    }
  }
}
</script>

<style lang="scss">

[data-pc-banner="flexiquote"] {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 33;
  max-height: 4.5rem; // cap at ~3 lines
  overflow: hidden;
}

#app,
#auth-content {
  overflow: hidden;
  min-height: 100vh; // minimum height to prevent truncation of dropdown menu. Previous value 550px
  // font-family: 'Open Sans', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* Fixed top AppNav — sits below the pc-banner */
  > header {
    &.is-fixed-top {
      position: fixed;
      top: var(--pc-banner-height, 0px);
      width: 100%;
      z-index: 32;
    }
  }
  > div.app-content {
    top: calc(3.25rem + var(--pc-banner-height, 0px));
    position: fixed;
    width: 100%;
    overflow-y: scroll !important;
    overflow-x: hidden !important;
    bottom: 0;
  }
}

#view-container {
  padding: 10px;
}

</style>
